import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { mapToExistingPrepaymentForm } from 'pages/invoices/util/InvoicesPageUtil';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateExistingPrepaymentsMutation } from 'store/api/invoices';
import {
    ExistingPrepaymentDto,
    ExistingPrepaymentListForm,
    InvoiceData,
    InvoiceStatusType,
    PrepaymentDataListItem,
} from 'types/invoices';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import ExistingPaymentsList from './ExistingPaymentsList';

interface Props {
    isInViewMode: boolean;
    invoice: InvoiceData;
    setSearchText: (value: string) => void;
    searchText: string;
    existingPrepayments?: PrepaymentDataListItem[];
}
const ExistingPaymentsCard: React.FC<Props> = ({
    isInViewMode,
    invoice,
    searchText,
    setSearchText,
    existingPrepayments,
}) => {
    // NOTE: searchBarState is the immediate input buffer for the debounced search.
    // It only derives the *initial* value from `searchText` — subsequent updates
    // are independent (typed value vs debounced parent state), so this is not a
    // true derived-state pattern.
    const [searchBarState, setSearchBarState] = useState(searchText);

    const canRemovePrepaymentFromPaidInvoice = useHasPermissions(['UPDATE.INVOICES.STATE_PAID.REMOVE_PREPAYMENT']);
    const canRemovePrepaymentFromSentInvoice = useHasPermissions(['UPDATE.INVOICES.STATE_SENT.REMOVE_PREPAYMENT']);

    const isAuthorizedToUpdatePrepayments =
        (invoice.invoiceStatusTypeCode === InvoiceStatusType.PAID && canRemovePrepaymentFromPaidInvoice) ||
        (invoice.invoiceStatusTypeCode === InvoiceStatusType.SENT && canRemovePrepaymentFromSentInvoice);

    const isReadonly = isInViewMode && !isAuthorizedToUpdatePrepayments;

    const handleSearchBarChange = useCallback((value = '') => {
        setSearchBarState(value);
    }, []);

    const handleSearch = useCallback(
        (searchText: string) => {
            setSearchText(searchText);
        },
        [setSearchText]
    );

    const handleSearchDebounced = useDebouncedCallback((value = '') => {
        handleSearch(value);
    }, 600);

    const [updateExistingPrepayments] = useUpdateExistingPrepaymentsMutation();
    const { handleSubmit, control, setValue } = useForm<ExistingPrepaymentListForm>({
        defaultValues: { existingPrepayments: mapToExistingPrepaymentForm(existingPrepayments || []) },
    });

    // Sync RTK Query result into RHF form state. setValue is an RHF API and
    // not a "lift state via callback" anti-pattern.
    useEffect(() => {
        if (existingPrepayments) {
            setValue('existingPrepayments', mapToExistingPrepaymentForm(existingPrepayments));
        }
    }, [existingPrepayments, setValue]);

    const handleOnSubmit = useCallback(
        () =>
            handleSubmit((form) => {
                try {
                    const dtoList: ExistingPrepaymentDto[] = (form.existingPrepayments ?? []).map((value) => ({
                        id: value.id,
                        remainingAmount: value.remainingAmount,
                        isConnected: value.isConnected,
                        payments: value.payments,
                    }));
                    updateExistingPrepayments({ id: invoice.id, dtoList }).unwrap().catch(console.error);
                } catch (e) {
                    console.error('onSubmit error: ', e);
                    errorAlert(t('forms.error-submitting-the-form'));
                }
            })(),
        [handleSubmit, invoice.id, updateExistingPrepayments]
    );

    return (
        <div className="flex flex-col gap-4">
            <SearchBar
                value={searchBarState}
                onChange={(event) => {
                    const value = event.target.value;
                    handleSearchBarChange(value);
                    handleSearchDebounced(value);
                }}
                pluralTextId=""
                singularTextId=""
                isInViewMode={isReadonly}
                placeHolder={t('invoices.general-tab.existing-payments.search-placeholder')}
            />
            <ExistingPaymentsList
                paymentCurrency={invoice.paymentCurrencyTypeCode}
                isInViewMode={isReadonly}
                control={control}
                setValue={setValue}
                onSubmit={() => {
                    void handleOnSubmit();
                }}
            />
        </div>
    );
};

export default ExistingPaymentsCard;
